import { Card, EmptyState, Table, Text, type Column } from "@marekl/adminer/components";
import { formatDateTime } from "@marekl/adminer/format";
import { useCertificateEmails, EMAIL_STATE_LABELS, type CertificateEmail } from "../../lib/queries/emails";

const EMPTY = "Certifikátu zatím nebyl odeslán žádný e-mail.";

interface CertificateEmailsCardProps {
  certificateId: number;
  /** Link the e-mails to letterman's admin UI (superadmins: they have letterman accounts). */
  showLettermanLinks: boolean;
}

/** The certificate's e-mails as letterman has them. */
export function CertificateEmailsCard({ certificateId, showLettermanLinks }: CertificateEmailsCardProps) {
  const result = useCertificateEmails(certificateId);
  const items = result.data?.items ?? [];

  const columns: Column<CertificateEmail>[] = [
    { key: "to", header: "Příjemce", primary: true, render: (row) => row.to },
    {
      key: "created_at",
      header: "Vytvořeno",
      render: (row) => <Text variant="mono">{formatDateTime(row.created_at)}</Text>,
    },
    { key: "state", header: "Stav", render: (row) => EMAIL_STATE_LABELS[row.state] ?? row.state },
    {
      key: "sent_at",
      header: "Čas odeslání",
      render: (row) =>
        row.sent_at ? <Text variant="mono">{formatDateTime(row.sent_at)}</Text> : <Text variant="muted">—</Text>,
    },
    ...(showLettermanLinks
      ? [
          {
            key: "letterman",
            header: "",
            render: (row: CertificateEmail) => (
              <a href={row.letterman_url} target="_blank" rel="noreferrer">
                V Lettermanu
              </a>
            ),
          },
        ]
      : []),
  ];

  return (
    <Card header={<Card.Header title="E-maily" subtitle="doručování přes Letterman" />} bodyPadding="none">
      <Table
        columns={columns}
        rows={items}
        rowKey={(row) => row.id}
        cards
        isLoading={result.isLoading}
        error={result.isError ? result.error : undefined}
        onRetry={() => void result.refetch()}
        // Never claim no e-mail was sent while the list is still unknown: a
        // manager might then resend a duplicate.
        empty={result.isLoading ? undefined : <EmptyState title="Žádné e-maily" description={EMPTY} />}
      />
    </Card>
  );
}
